import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { homeCardsApi } from '@/lib/api'
import { HomeCard, HomeCardCreate } from '@/types'
import { Plus, Trash2, ChevronUp, ChevronDown, Pencil, X, Check, LayoutGrid, Lock } from 'lucide-react'

// ── Icon catalogue (icon_name → display label + emoji preview) ───────────────
const ICONS = [
  { name: 'description_outlined', label: 'Formulários', emoji: '📋' },
  { name: 'task_alt', label: 'Tarefas', emoji: '✅' },
  { name: 'person_outline', label: 'Perfil', emoji: '👤' },
  { name: 'cloud_sync_outlined', label: 'Sincronizar', emoji: '🔄' },
  { name: 'inventory_2_outlined', label: 'Inventário', emoji: '📦' },
  { name: 'bar_chart', label: 'Relatórios', emoji: '📊' },
  { name: 'schedule', label: 'Agenda', emoji: '📅' },
  { name: 'qr_code_scanner', label: 'Scanner', emoji: '📷' },
  { name: 'map_outlined', label: 'Mapa', emoji: '🗺️' },
  { name: 'notification_important_outlined', label: 'Alertas', emoji: '🔔' },
  { name: 'settings_outlined', label: 'Definições', emoji: '⚙️' },
  { name: 'widgets', label: 'Outro', emoji: '🔷' },
]

const ROUTES = [
  { value: '/forms', label: 'Formulários' },
  { value: '/tasks', label: 'Tarefas' },
  { value: '/settings/profile', label: 'Perfil' },
  { value: 'sync', label: 'Sincronizar (ação)' },
  { value: '/reports', label: 'Relatórios' },
  { value: '/scanner', label: 'Scanner' },
  { value: '/inventory', label: 'Inventário' },
]

const COLORS = [
  '#2563EB', '#16A34A', '#EA580C', '#9333EA',
  '#0891B2', '#DC2626', '#CA8A04', '#059669',
]

const PERMISSIONS = [
  { value: '', label: 'Todos os utilizadores' },
  { value: 'forms.view', label: 'Ver Formulários' },
  { value: 'tasks.view', label: 'Ver Tarefas' },
  { value: 'reports.view', label: 'Ver Relatórios' },
  { value: 'inventory.view', label: 'Ver Inventário' },
  { value: 'admin', label: 'Apenas Admins' },
]

const iconEmoji = (name: string) => ICONS.find(i => i.name === name)?.emoji ?? '🔷'
const routeLabel = (r: string) => ROUTES.find(x => x.value === r)?.label ?? r

// ── Card form modal ───────────────────────────────────────────────────────────
function CardModal({
  initial, onSave, onClose,
}: {
  initial?: Partial<HomeCard>
  onSave: (data: HomeCardCreate) => void
  onClose: () => void
}) {
  const [form, setForm] = useState<HomeCardCreate>({
    title: initial?.title ?? '',
    icon_name: initial?.icon_name ?? 'widgets',
    route: initial?.route ?? '/forms',
    color_hex: initial?.color_hex ?? '#2563EB',
    sort_order: initial?.sort_order ?? 0,
    required_permission: initial?.required_permission ?? null,
    is_active: initial?.is_active ?? true,
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{initial?.id ? 'Editar card' : 'Novo card'}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
            <input className="input w-full" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Formulários" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ícone</label>
            <div className="grid grid-cols-6 gap-2">
              {ICONS.map(ic => (
                <button key={ic.name}
                  onClick={() => setForm(f => ({ ...f, icon_name: ic.name }))}
                  title={ic.label}
                  className={`rounded-lg p-2 text-xl text-center transition ${form.icon_name === ic.name ? 'ring-2 ring-primary-500 bg-primary-50' : 'hover:bg-gray-100'}`}>
                  {ic.emoji}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destino</label>
            <select className="input w-full" value={form.route}
              onChange={e => setForm(f => ({ ...f, route: e.target.value }))}>
              {ROUTES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, color_hex: c }))}
                  style={{ backgroundColor: c }}
                  className={`h-8 w-8 rounded-full transition ${form.color_hex === c ? 'ring-2 ring-offset-2 ring-gray-800 scale-110' : ''}`} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Lock className="h-3.5 w-3.5" /> Permissão necessária
            </label>
            <select className="input w-full"
              value={form.required_permission ?? ''}
              onChange={e => setForm(f => ({ ...f, required_permission: e.target.value || null }))}>
              {PERMISSIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
            Card ativo
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary flex items-center gap-2"
            onClick={() => { if (form.title && form.route) onSave(form) }}>
            <Check className="h-4 w-4" /> Guardar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HomeCardsPage() {
  const queryClient = useQueryClient()
  const [editCard, setEditCard] = useState<HomeCard | null | 'new'>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['home-cards-admin'],
    queryFn: () => homeCardsApi.listAdmin(),
  })
  const cards: HomeCard[] = data?.data ?? []

  const createMut = useMutation({
    mutationFn: homeCardsApi.create,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['home-cards-admin'] }); setEditCard(null) },
  })
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<HomeCardCreate> }) => homeCardsApi.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['home-cards-admin'] }); setEditCard(null) },
  })
  const deleteMut = useMutation({
    mutationFn: homeCardsApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['home-cards-admin'] }),
  })
  const reorderMut = useMutation({
    mutationFn: homeCardsApi.reorder,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['home-cards-admin'] }),
  })

  const move = (card: HomeCard, dir: -1 | 1) => {
    const sorted = [...cards].sort((a, b) => a.sort_order - b.sort_order)
    const idx = sorted.findIndex(c => c.id === card.id)
    const swap = sorted[idx + dir]
    if (!swap) return
    reorderMut.mutate([
      { id: card.id, sort_order: swap.sort_order },
      { id: swap.id, sort_order: card.sort_order },
    ])
  }

  const sorted = [...cards].sort((a, b) => a.sort_order - b.sort_order)

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutGrid className="h-6 w-6 text-primary-600" /> Ecrã Principal — Cards
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Configura os cards visíveis no Android e as permissões por role. A ordem aqui é a ordem no telemóvel.
          </p>
        </div>
        <button className="btn btn-primary flex items-center gap-2" onClick={() => setEditCard('new')}>
          <Plus className="h-4 w-4" /> Novo card
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" /></div>
        ) : sorted.length === 0 ? (
          <div className="card py-16 text-center text-gray-400">
            <LayoutGrid className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p>Sem cards configurados. Cria o primeiro.</p>
          </div>
        ) : sorted.map((card, idx) => (
          <div key={card.id} className={`card flex items-center gap-4 transition ${!card.is_active ? 'opacity-50' : ''}`}>
            {/* Color + icon */}
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl"
              style={{ backgroundColor: card.color_hex + '22' }}>
              {iconEmoji(card.icon_name)}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900">{card.title}</p>
              <p className="text-xs text-gray-400">{routeLabel(card.route)}</p>
              {card.required_permission && (
                <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                  <Lock className="h-3 w-3" /> {card.required_permission}
                </span>
              )}
            </div>

            {/* Status */}
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${card.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {card.is_active ? 'Ativo' : 'Inativo'}
            </span>

            {/* Color swatch */}
            <div className="h-5 w-5 rounded-full shrink-0" style={{ backgroundColor: card.color_hex }} />

            {/* Actions */}
            <div className="flex items-center gap-1">
              <button disabled={idx === 0} onClick={() => move(card, -1)}
                className="rounded p-1 hover:bg-gray-100 disabled:opacity-30">
                <ChevronUp className="h-4 w-4" />
              </button>
              <button disabled={idx === sorted.length - 1} onClick={() => move(card, 1)}
                className="rounded p-1 hover:bg-gray-100 disabled:opacity-30">
                <ChevronDown className="h-4 w-4" />
              </button>
              <button onClick={() => setEditCard(card)}
                className="rounded p-1 text-blue-600 hover:bg-blue-50">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => { if (confirm(`Eliminar "${card.title}"?`)) deleteMut.mutate(card.id) }}
                className="rounded p-1 text-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview */}
      {sorted.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-sm font-semibold text-gray-600 uppercase tracking-wide">Pré-visualização Android</h2>
          <div className="inline-block rounded-3xl bg-gray-900 p-4 shadow-xl">
            <div className="w-64 rounded-2xl bg-gray-100 overflow-hidden">
              <div className="bg-[#1A1A2E] px-3 py-2 flex items-center gap-2">
                <div className="h-1 flex-1 rounded bg-green-400 opacity-70 text-xs text-green-300 leading-none flex items-center pl-1">WiFi · Sem sync</div>
                <span className="text-[10px] text-white/40">v1.0</span>
              </div>
              <div className="p-3">
                <p className="mb-2 text-xs font-bold text-gray-700">Olá, Utilizador</p>
                <div className="grid grid-cols-2 gap-2">
                  {sorted.filter(c => c.is_active).map(card => (
                    <div key={card.id} className="rounded-xl bg-white p-3 shadow-sm flex flex-col items-center gap-1.5">
                      <div className="h-9 w-9 rounded-full flex items-center justify-center text-lg"
                        style={{ backgroundColor: card.color_hex + '22' }}>
                        {iconEmoji(card.icon_name)}
                      </div>
                      <p className="text-[10px] font-semibold text-gray-700 text-center leading-tight">{card.title}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {editCard !== null && (
        <CardModal
          initial={typeof editCard === 'string' ? {} : editCard}
          onSave={d => {
            if (typeof editCard === 'string') createMut.mutate({ ...d, sort_order: cards.length })
            else if (editCard !== null) updateMut.mutate({ id: editCard.id, data: d })
          }}
          onClose={() => setEditCard(null)}
        />
      )}
    </div>
  )
}
